import { useParams, Link, Navigate } from 'react-router-dom';
import { categories, games, categoryPageData } from '@/mocks/gameData';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  if (!slug || !categoryPageData[slug]) {
    return <Navigate to="/" replace />;
  }

  const category = categories.find((c) => c.slug === slug);
  const pageData = categoryPageData[slug];
  const categoryGames = games.filter((g) => g.category === slug);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: pageData.title,
            description: pageData.description,
            url: `https://hdyxdaquan.com/category/${slug}`,
            isPartOf: { '@id': 'https://hdyxdaquan.com/#website' },
          }),
        }}
      />

      <div className="pt-20 pb-10 bg-background-50">
        <div className="w-full px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '游戏分类', href: '/category/rpg' },
              { label: category?.name || slug },
            ]}
          />

          <div className="mt-4 mb-10">
            <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-3">{pageData.title}</h1>
            <p className="text-sm text-foreground-500 max-w-2xl">{pageData.description}</p>
          </div>

          {/* Category Tabs */}
          <div className="flex items-center gap-2 overflow-x-auto pb-4 mb-8 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {/* Game List */}
          {categoryGames.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-product-shop="">
              {categoryGames.map((game) => (
                <Link
                  key={game.slug}
                  to={`/article/${game.slug}`}
                  className="group bg-background-50 border border-background-200 rounded-xl overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img src={game.imageUrl} alt={game.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 bg-background-50/90 backdrop-blur-sm rounded-full">
                      <i className="ri-star-fill text-amber-400 text-xs"></i>
                      <span className="text-xs font-medium text-foreground-700">{game.rating}</span>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading text-base text-foreground-900 mb-2 group-hover:text-primary-500 transition-colors">{game.title}</h3>
                    <p className="text-sm text-foreground-500 line-clamp-2 mb-3">{game.description}</p>
                    <div className="flex items-center flex-wrap gap-1.5 mb-3">
                      {game.tags.map((tag) => (
                        <span key={tag} className="px-2 py-0.5 bg-secondary-100 text-secondary-700 text-xs rounded-full whitespace-nowrap">{tag}</span>
                      ))}
                    </div>
                    <div className="flex items-center justify-between text-xs text-foreground-400">
                      <span>{game.developer}</span>
                      <span>{game.platform.join(' / ')}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100 text-foreground-300">
                <i className="ri-gamepad-line text-2xl"></i>
              </div>
              <p className="text-sm text-foreground-400">该分类暂无游戏，请浏览其他分类</p>
            </div>
          )}
        </div>
      </div>

      {/* Category FAQ */}
      <section className="py-14 bg-background-100">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-6 text-center">关于{category?.name}的常见问题</h2>
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify({
                  '@context': 'https://schema.org',
                  '@type': 'FAQPage',
                  mainEntity: pageData.faq.map((f) => ({
                    '@type': 'Question',
                    name: f.question,
                    acceptedAnswer: { '@type': 'Answer', text: f.answer },
                  })),
                }),
              }}
            />
            <div className="space-y-3">
              {pageData.faq.map((faq, i) => (
                <details key={i} className="group bg-background-50 rounded-xl border border-background-200">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                    <span className="text-sm font-medium text-foreground-800 pr-4">{faq.question}</span>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-500 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Related Categories */}
      <section className="py-14 bg-background-50">
        <div className="w-full px-4 md:px-6">
          <div className="text-center mb-8">
            <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-3">浏览其他分类</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 max-w-5xl mx-auto">
            {categories.filter(c => c.slug !== slug).map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="group bg-background-100 rounded-xl p-4 text-center hover:bg-primary-500 transition-all duration-300 cursor-pointer"
              >
                <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 group-hover:bg-background-50/20 group-hover:text-background-50 transition-all">
                  <i className={`${cat.icon} text-lg`}></i>
                </div>
                <span className="text-xs font-medium text-foreground-700 group-hover:text-background-50 whitespace-nowrap">{cat.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}